import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { accountTypes } from 'config/user.type.constants'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button, MultiSelect, Select } from 'stories/components'
import { confirm } from 'utils'

const USER_EMAIL_OPTION = 'self'

export type DefaultSenderEmailGroup = {
  accountTypes: string[]
  defaultSenderEmail: string
}

export const DefaultSenderEmailSettings = () => {
  const emailOptions = useSelector(
    (state: any) => state.globalConfigReducer[GlobalConfigType.EMAIL_OPTIONS] || {},
  ) as Record<string, string>

  const [isLoading, setLoading] = useState(true)
  const [isChanged, setChanged] = useState(false)
  const [data, setData] = useState<DefaultSenderEmailGroup[]>([])

  const senderOptions = useMemo(
    () => ({
      ...emailOptions,
      [USER_EMAIL_OPTION]: "User's Email (the sender)",
    }),
    [emailOptions],
  )

  useEffect(() => {
    setLoading(true)
    getAdminConfig('defaultSenderEmailByAccountType')
      .then((res) => {
        if (Array.isArray(res) && res.length) {
          setData(
            res.map((item: DefaultSenderEmailGroup) => ({
              accountTypes: item.accountTypes || [],
              defaultSenderEmail: item.defaultSenderEmail || 'HOME_EMAIL',
            })),
          )
        } else {
          setData([{ accountTypes: [], defaultSenderEmail: 'HOME_EMAIL' }])
        }
      })
      .finally(() => setLoading(false))
  }, [])

  const onChange = (index: number, field: keyof DefaultSenderEmailGroup, value: any) => {
    const next = cloneDeep(data)
    ;(next[index] as any)[field] = value
    setData(next)
    setChanged(true)
  }

  const onAdd = () => {
    const next = cloneDeep(data)
    next.push({
      accountTypes: [],
      defaultSenderEmail: 'HOME_EMAIL',
    })
    setData(next)
    setChanged(true)
  }

  const onRemove = async (index: number) => {
    if (index === 0) {
      toast('Cannot remove the default group', { type: 'error' })
      return
    }
    const result = await confirm('Are you sure you want to remove this group?')
    if (!result) return

    const next = cloneDeep(data)
    next.splice(index, 1)
    setData(next)
    setChanged(true)
  }

  const onSave = () => {
    setLoading(true)
    setAdminConfig('defaultSenderEmailByAccountType', data)
      .then(() => {
        toast('Default sender email settings saved.', { type: 'success' })
        setChanged(false)
      })
      .finally(() => setLoading(false))
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <SaveChanges show={isChanged} onSave={onSave} />
      <div className="mb-4 flex items-center justify-between gap-4">
        <p className="text-sm text-gray-600">
          Map account types to a default sender email. The first group is the fallback when no other group matches.
          Users can still change this in My Profile → Configurations.
        </p>
        <Button link onClick={onAdd}>
          <span className="flex gap-2 items-center whitespace-nowrap">
            <PlusIcon className="w-5 h-5" />
            Add Group
          </span>
        </Button>
      </div>

      <div className="space-y-4 max-w-4xl">
        {data.map((group, index) => {
          const isDefault = index === 0
          return (
            <div key={index} className="border rounded-lg p-4 bg-white shadow-sm">
              <div className="flex items-start gap-4">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {!isDefault ? (
                    <MultiSelect
                      id={`default-sender-email-account-types-${index}`}
                      title="Account Types"
                      options={accountTypes}
                      value={group.accountTypes || []}
                      onChange={(selected) => onChange(index, 'accountTypes', selected)}
                      className="w-full"
                    />
                  ) : (
                    <div className="flex items-end pb-2">
                      <span className="text-sm font-medium text-gray-700">Default (all unmatched account types)</span>
                    </div>
                  )}
                  <Select
                    id={`default-sender-email-${index}`}
                    title="Email From"
                    options={senderOptions}
                    value={group.defaultSenderEmail || 'HOME_EMAIL'}
                    onChange={(value: string) => onChange(index, 'defaultSenderEmail', value)}
                    sort
                    className="w-full"
                  />
                </div>
                {!isDefault && (
                  <button
                    className="text-red-500 hover:text-red-700 p-2 mt-6"
                    onClick={() => onRemove(index)}
                    title="Remove group"
                    type="button"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
